import type { EditableComsFields } from '../IncidentComsTable';
import type { PlannedResource } from 'components/IAP/hooks/assignmentListHooks';
import type { Status } from './assignmentStatus';

/**
 * Draft shapes for the ICS-204 edit form.
 *
 * The card edits three entities at once, so the form holds drafts rather than
 * writing through on change. Rows that exist on the server carry their `id`; a
 * blank `id` marks a row the user just added. Deletes are represented as
 * `archived` because none of the three entities has a DELETE endpoint.
 */

export type ResourceNeedDraft = {
  resourceName: string;
  required: number | string;
  have: number | string;
  need: number | string;
};

export type WorkAssignmentDraft = {
  id: string;
  name: string;
  overheadPosition: string;
  specialInstructions: string;
  reportingLocation: string;
  /** ISO string, or empty when unset. */
  requestedArrivalTime: string;
  resourceNeeds: ResourceNeedDraft[];
  archived: boolean;
};

export type CommunicationDraft = EditableComsFields & {
  id: string;
  archived: boolean;
};

export type PlannedResourceDraft = {
  resourceName: string;
  leader: string;
  numberOfPersons: number;
  contactInfo: string;
  reportingInfo: string;
  has204a: boolean;
  isDocumentationEntry?: boolean;
  resourceBranch?: string;
  resourceDivision?: string;
  resourceGroup?: string;
  resourceStrikeTeam?: string;
  resourceTaskForce?: string;
  resourceAssignmentLocation?: string;
  resourceSpecialInstructions?: string;
  resourceSafetyPlanLocation?: string;
};

export type AssignmentCardFormData = {
  walaId: string | null;
  sectionChief: string;
  sectionChiefContact?: string;
  workGroupId: string;
  branch: string;
  division: string;
  walaGroup: string;
  stagingArea: string;
  proposedStatus: Status;
  branchDirector?: string;
  branchDirectorContact?: string;
  divisionGroupSupervisor?: string;
  divisionGroupSupervisorContact?: string;
  resource?: Record<string, PlannedResourceDraft>;
  workAssignments: WorkAssignmentDraft[];
  communications: CommunicationDraft[];
  medicalCommunication?: string;
  evacuationCommunication?: string;
  otherCommunication?: string;
  specialInstructions?: string;
};

export const emptyWorkAssignmentDraft = (): WorkAssignmentDraft => ({
  id: '',
  name: '',
  overheadPosition: '',
  specialInstructions: '',
  reportingLocation: '',
  requestedArrivalTime: '',
  resourceNeeds: [],
  archived: false,
});

export const emptyResourceNeedDraft = (): ResourceNeedDraft => ({
  resourceName: '',
  required: '',
  have: '',
  need: '',
});

/**
 * Seeds a resource the user just picked. `resourceName` is display-only for
 * linked resources -- the field is disabled in the row and `persist` never
 * sends it -- but it has to be carried so the new row renders with a name
 * before the card is saved. Documentation entries keep the name editable and
 * persist it as `documentationName`.
 */
export const emptyPlannedResourceDraft = (
  resourceName: string,
  options?: { isDocumentationEntry?: boolean }
): PlannedResourceDraft => ({
  resourceName,
  leader: '',
  numberOfPersons: 0,
  contactInfo: '',
  reportingInfo: '',
  has204a: false,
  isDocumentationEntry: options?.isDocumentationEntry ?? false,
});

/** Maps a form draft entry into the WALA plannedResources API shape. */
export const plannedResourceFromDraft = (
  id: string,
  res: PlannedResourceDraft
): PlannedResource => ({
  id,
  leader: res.leader,
  numberOfPersons: Number(res.numberOfPersons),
  contactInfo: res.contactInfo,
  reportingInfo: res.reportingInfo,
  has204a: !!res.has204a,
  isDocumentationEntry: !!res.isDocumentationEntry,
  documentationName: res.isDocumentationEntry ? res.resourceName : null,
  resourceBranch: res.resourceBranch,
  resourceDivision: res.resourceDivision,
  resourceGroup: res.resourceGroup,
  resourceStrikeTeam: res.resourceStrikeTeam,
  resourceTaskForce: res.resourceTaskForce,
  resourceAssignmentLocation: res.resourceAssignmentLocation,
  resourceSpecialInstructions: res.resourceSpecialInstructions,
  resourceSafetyPlanLocation: res.resourceSafetyPlanLocation,
});

export const emptyCommunicationDraft = (): CommunicationDraft => ({
  id: '',
  archived: false,
  type: 'Radio',
  channel_number: '',
  function: '',
  channel_name: '',
  work_group_id: '',
  rx_frequency: '',
  rx_tone_nac: '',
  tx_frequency: '',
  tx_tone_nac: '',
  mode: '',
  remarks: '',
  cell_phone_number: '',
  other: '',
});
